import { useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import { DraftGuardContext, type DraftSaveHandle } from "./draftGuardContext";

/** Compare the complete editable value with the last successfully saved snapshot. */
export function useUnsavedDraft(value: unknown, priority = 0) {
  const snapshot = JSON.stringify(value);
  const [savedSnapshot, setSavedSnapshot] = useState(snapshot);
  const dirty = snapshot !== savedSnapshot;
  const saveHandle = useRef<DraftSaveHandle | null>(null);
  const [saveable, setSaveable] = useState(false);
  const connectSaver = useCallback((handle: DraftSaveHandle | null) => {
    saveHandle.current = handle;
    setSaveable(handle !== null);
  }, []);
  const id = useId();
  const { register, requestClose } = useContext(DraftGuardContext);
  const save = useCallback(() => saveHandle.current?.save() ?? Promise.resolve(false), []);
  useEffect(() => register(id, { dirty, saveable, save, priority }), [register, id, dirty, saveable, save, priority]);
  return {
    dirty,
    connectSaver,
    // This closure records the submitted render, including when edits arrive during save.
    markSaved: () => { setSavedSnapshot(snapshot); },
    requestClose: (close: () => void, allEditors = false) => { requestClose(close, allEditors ? undefined : id); },
  };
}
